import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { logout } from '../store/authSlice';
import axiosInstance from '../axiosInstance';

/**
 * Hard logout. Server tells Sanctum to drop the session cookie + revoke
 * the bearer token, the browser then wipes every scrap of identity state
 * and hard-reloads to /login. Same nuclear pattern used by the admin
 * impersonation flow (see src/impersonation.js) — anything less and a
 * stale entry in localStorage / sessionStorage / a service-worker cache
 * can leak the previous user into the next login.
 *
 * Things explicitly cleared:
 *   - bearer token (localStorage.auth_token, sessionStorage.auth_token)
 *   - redux-persist root (persist:root, persist:auth)
 *   - saved-accounts list (saved_accounts)
 *   - acting-as creator state (acting_as, original_token, original_user)
 *   - install gate cache (install_status, impersonation_pending)
 *   - cached theme palette + branding sessionStorage
 *   - service-worker runtime cache (cached /api/user responses, shells)
 *
 * Things deliberately preserved:
 *   - color_theme (user's light/dark choice — survives logout)
 *   - i18nextLng    (user's language preference — survives logout)
 */
const Logout = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;

    const fullLogout = async () => {
      // 1. Backend logout. Tells Sanctum to invalidate the session cookie
      //    + revoke the current bearer token + send an explicit
      //    cookie-forget Set-Cookie. Fire-and-forget — we still wipe
      //    client state and navigate even if this fails (network blip,
      //    expired token, etc.).
      try {
        await axiosInstance.post('/logout');
      } catch (_) { /* don't block client cleanup */ }

      if (cancelled) return;

      // 2. Wipe localStorage but preserve a tiny allowlist of pure-prefs.
      try {
        const preserveLocal = {};
        const preserveKeys = ['color_theme', 'i18nextLng'];
        preserveKeys.forEach((k) => {
          const v = localStorage.getItem(k);
          if (v !== null) preserveLocal[k] = v;
        });
        localStorage.clear();
        Object.entries(preserveLocal).forEach(([k, v]) => localStorage.setItem(k, v));
      } catch (_) {}

      // 3. Wipe sessionStorage entirely — nothing here should survive logout.
      try { sessionStorage.clear(); } catch (_) {}

      // 4. Purge service-worker runtime cache so a stale /api/user or
      //    /api/site-config response can't repopulate the prior identity.
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch (_) {}

      // 5. Reset Redux (this also calls storage.removeItem("persist:root")
      //    via the reducer — belt-and-suspenders alongside the clear above).
      dispatch(logout());

      // 6. Hard reload to /login. Using window.location instead of
      //    react-router navigate so the entire React tree, AppContext,
      //    in-memory caches, and any registered service-worker controller
      //    re-initialise from a clean slate. No identity bleed possible.
      window.location.replace('/login');
    };

    fullLogout();
    return () => { cancelled = true; };
  }, [dispatch]);

  return null;
};

export default Logout;
